

import { AgGridReact } from "ag-grid-react";
import { AsyncActionState } from "modus/components/form/async-actions/AsyncAction.type";
import React, { useEffect, useRef, useState } from "react";
import { IdentityApplication } from "../services/ApplicationService";
import { Box, Link, Typography, Paper, makeStyles, createStyles, Theme } from "@material-ui/core";
import ApplicationIcon from "legacy-components/icons/ApplicationIcon";
import ServiceApplicationIcon from "legacy-components/icons/ServiceApplicationIcon";
import { Date, Email } from "modus/components/list/table/TableUtils";
import { ApplicationRemover } from "../pages/application-summary-page/ApplicationRemover";
import { useHistory } from "react-router";
import { useSelector } from "react-redux";
import Loader from "modus/components/misc/Loader";
import { ErrorMessage, resolveAPIErrorMessage } from "modus/components/error-message/ErrorMessage";
import { GridApi, GridReadyEvent } from "ag-grid-community";
import ResizeObserver from 'resize-observer-polyfill';
import Authorizer from "modus/components/authorizer/Authorizer";
import clsx from "clsx";

const useGridStyles = makeStyles((theme: Theme) => {
    return createStyles({
        root: {
            position: 'relative',
            width: '100%',
            overflow: 'hidden',
            height: (props: { calculatedHeight: string | number }) => props.calculatedHeight,
            "&.ag-theme-alpine": {
                fontFamily: theme.typography.fontFamily,
                fontSize: theme.typography.fontSize,
                fontWeight: theme.typography.fontWeightRegular,
                color: theme.palette.text.primary,

                "& .ag-root-wrapper": {
                    border: "none"
                },

                "& .ag-header": {
                    backgroundColor: "#EAEAEF",
                    borderColor: theme.palette.divider,

                    "& .ag-header-row": {
                        color: theme.palette.text.primary,                        
                        fontWeight: theme.typography.fontWeightMedium
                    }
                },

                "& .ag-header-cell": {
                    paddingLeft: theme.spacing(3),
                    paddingRight: theme.spacing(3)
                },

                "& .ag-pinned-right-header": {
                    border: "none",

                    "& .ag-header-row": {
                        "&::after": {
                            display: "none"
                        }
                    }
                },

                "& .ag-row": {
                    border: "none"
                },

                "& .ag-cell": {
                    border: "none!important",
                    paddingLeft: theme.spacing(3),
                    paddingRight: theme.spacing(3),
                    lineHeight: "42px"
                },

                "& .form-selection-grid__action-cell": {
                    padding: theme.spacing(0, 2, 0, 0),
                    textAlign: "right"
                },

                "& .ag-cell .ag-react-container": {
                    width: '100%',
                    height: '100%',
                }
            }
        },
        header: {
            "&--tinted": {
                "&.ag-theme-alpine": {
                    "& .ag-header": {
                        backgroundColor: theme.palette.background.default,
                    }
                }
            }
        },
        clickable: {
            "&.ag-theme-alpine": {
                "& .ag-row": {
                    cursor: "pointer",
                    transition: theme.transitions.create("backgroundColor"),

                    "&:hover": {
                        backgroundColor: theme.palette.action.hover,
                    },

                    "&:active": {
                        backgroundColor: theme.palette.action.selected,
                    }
                },
            }
        }
    });
});

interface AppListViewProps {
    state: AsyncActionState<IdentityApplication[]| null>;
    applicationList?: IdentityApplication[]|null;
    refresh?: ()=> void;
}



const Type = ({ value }: { value: any }) => {
    return (
        <Box key={value}     style={{height: '100%',
        display: 'flex',
        justifyItems: 'center',
        alignItems: 'center'}} >
            {value?.name === "application" ? <ApplicationIcon style={{color:'#0E416C'}}/>: <ServiceApplicationIcon style={{color:'#0E416C'}} />}
        </Box>
        
    )
}


export const AppTitleLink: React.FC<{ formattedValue?: any , value?: any, data?: any }> =
  React.memo(({  value, formattedValue, data }) => {
    const history = useHistory();
    const identityEnvironment = useSelector((state: any) => state?.common?.identityEnvironment );
    const onClick = () => {
        // data contains the full row data
        history.push(`/home/applications/${data.id}/gateway/${identityEnvironment}/summary?applicationName=${encodeURIComponent(data.applicationName)}`, data.id);    }
    return (
      <Box key={value}  display={"flex"}  alignItems={"center"} height="100%">
        <Link onClick={onClick}>
        <Typography variant="body1" style={{color: "#0E416C", fontSize:"14px", fontWeight: 600}}>
          {data?.applicationName}
        </Typography>
        </Link>
      </Box>
    );
  });

export const AppListView:React.FC<AppListViewProps> = ({state, refresh, applicationList}) => {
    const rowHeight = 48; // px (matching existing rowHeight)
    const headerHeight = 48; // px (matching existing headerHeight)
    const padding = 2; // px (optional padding)
    
    const [maxHeight, setMaxHeight] = useState(window.innerHeight - 375);

    useEffect(() => {
        const handleResize = () => {
            setMaxHeight(window.innerHeight - 325);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const calculatedHeight = React.useMemo(() => {
        if (!applicationList?.length) return 'auto';
        return Math.min(
            maxHeight,
            headerHeight + (applicationList.length * rowHeight) + (padding * 2)
        );
    }, [applicationList?.length, maxHeight]);

    const gridStyle = useGridStyles({ calculatedHeight });

    const AppRemover = React.memo(({ data }: { data: any }) => {
        return (
            <Box key={data.id} style={{
                height: '100%',
                display: 'flex',
                justifyItems: 'center',
                alignItems: 'center'
            }}>
                <ApplicationRemover application={data} iconOnly onSuccess={refresh}/>
            </Box>
        )
    });

    const gridRef = useRef(null);
    const containerRef = useRef<HTMLElement>(null);
    const [gridApi, setGridApi] = useState<GridApi>();

    const onGridReady = React.useCallback((params: GridReadyEvent) => {
        setGridApi(params.api);
        window.setTimeout(() => {
            params.api.sizeColumnsToFit();
        }, 100);
    }, []);

    useEffect(() => {
        const resizeObserver = new ResizeObserver(() => {
            if (gridApi) {
                gridApi?.sizeColumnsToFit();
                setTimeout(() => {
                    gridApi?.sizeColumnsToFit();
                }, 1000);
            }
        });

        if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
        }

        return () => {
            if (containerRef.current) {
                resizeObserver.unobserve(containerRef.current);
            }
        };
    }, [gridApi]);
    return (
        <Box>
            {state?.loading && (
                <Loader showBackDrop/>
            )}
            {!state?.loading && state?.value && applicationList && applicationList?.length > 0 && (
                <Paper 
                    ref={containerRef} 
                    elevation={2} 
                    className={clsx("ag-theme-alpine", gridStyle.root, gridStyle.header, gridStyle.clickable)}
                >
                <AgGridReact
                    ref={gridRef}
                    rowData={applicationList}
                    frameworkComponents={{Type,AppTitleLink, Date, AppRemover, Email}}
                    rowHeight={rowHeight}
                    headerHeight={headerHeight}
                    domLayout={'normal'}
                    onGridReady={onGridReady}
                    columnDefs={[
                        {
                            headerName: "Type",
                            field: "applicationType",
                            width: 100,
                            cellRenderer: "Type",
                            flex:0.5
                        },
                        {
                            headerName: "Name",
                            field: "applicationName",
                            cellRenderer: "AppTitleLink",
                            flex: 1,
                        },
                        {
                            headerName: "Created Date",
                            field: "created",
                            flex: 0.7,
                            cellRenderer: "Date"
                        },
                        {
                            headerName: "Created By",
                            field: "createdBy",
                            cellRenderer: "Email",
                            flex: 1.5,
                        },{
                            headerName: "Actions",
                            width: 150,
                            field: "applicationName",
                            cellRenderer:"AppRemover",
                            flex: 0.5
                        }
                    ]}
                />
                </Paper>
            )}
            {!state?.loading && state?.value && (!applicationList || applicationList?.length === 0) && (
                <ErrorMessage message={"No Applications found"}/>
            )}
            {state?.error && (
                <ErrorMessage message={resolveAPIErrorMessage(state?.error)}/>
            )}
        
        </Box>
    );
}